import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useIG } from "../lib/igContext";
import { Film, Plus, Trash2, Loader2, X, Filter, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const TAGS = [
  "Hook fort", "Trend", "Lifestyle", "Sexy soft", "Face cam",
  "Question directe", "Test", "Viral", "À refaire", "Mauvaise performance",
];

const TYPES = ["reel", "post", "carousel", "story"];

export default function ContentTable() {
  const { selected, accounts, isGlobal } = useIG();
  const [media, setMedia] = useState([]);
  const [filter, setFilter] = useState({ type: "", tag: "", scoreMin: 0 });
  const [showAdd, setShowAdd] = useState(false);

  const refresh = async () => {
    try {
      const list = selected ? await api.igListMedia(selected.id) : await api.igListMedia();
      setMedia(list);
    } catch { toast.error("Erreur"); }
  };

  useEffect(() => { refresh(); }, [selected?.id]);

  const filtered = useMemo(() => {
    return media.filter(m => {
      if (filter.type && m.media_type !== filter.type) return false;
      if (filter.tag && !(m.tags || []).includes(filter.tag)) return false;
      if (filter.scoreMin && m.performance_score < filter.scoreMin) return false;
      return true;
    });
  }, [media, filter]);

  const remove = async (id) => {
    if (!confirm("Supprimer ?")) return;
    await api.igDeleteMedia(id); refresh();
  };

  return (
    <div className="space-y-7 animate-fadeUp" data-testid="content-table-page">
      <header className="border-l-2 border-[#CCFF00] pl-5 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// CONTENUS PUBLIÉS</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Tableau Contenus</h1>
          <p className="text-zinc-400 mt-2">Tous les posts/reels avec score performance, tags, notes internes.</p>
        </div>
        <button data-testid="add-media-btn" onClick={() => setShowAdd(true)} disabled={accounts.length === 0}
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-30 flex items-center gap-2 text-sm glow-volt">
          <Plus className="w-4 h-4" /> Ajouter un contenu
        </button>
      </header>

      <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-3 flex gap-3 items-center flex-wrap">
        <span className="text-[10px] font-mono uppercase text-zinc-500 flex items-center gap-1"><Filter className="w-3 h-3" /> FILTRES</span>
        <select data-testid="f-type" value={filter.type} onChange={e => setFilter({...filter, type: e.target.value})}
          className="bg-[#070708] border border-[#1E1E24] rounded-sm px-2 py-1 text-xs text-white">
          <option value="">Tous types</option>
          {TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <select data-testid="f-tag" value={filter.tag} onChange={e => setFilter({...filter, tag: e.target.value})}
          className="bg-[#070708] border border-[#1E1E24] rounded-sm px-2 py-1 text-xs text-white">
          <option value="">Tous tags</option>
          {TAGS.map(t => <option key={t}>{t}</option>)}
        </select>
        <select data-testid="f-score" value={filter.scoreMin} onChange={e => setFilter({...filter, scoreMin: Number(e.target.value)})}
          className="bg-[#070708] border border-[#1E1E24] rounded-sm px-2 py-1 text-xs text-white">
          <option value="0">Tous scores</option>
          <option value="80">Excellent (≥80)</option>
          <option value="60">Bon (≥60)</option>
          <option value="40">Moyen (≥40)</option>
        </select>
        <span className="ml-auto text-[10px] font-mono text-zinc-500">{filtered.length} / {media.length}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
          <Film className="w-12 h-12 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
          <div className="text-zinc-500 text-sm">Aucun contenu — ajoute ton premier post/reel.</div>
        </div>
      ) : (
        <div className="border border-[#1E1E24] rounded-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0C] border-b border-[#1E1E24]">
              <tr className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Caption</th>
                <th className="text-right px-4 py-3">Vues</th>
                <th className="text-right px-4 py-3">Reach</th>
                <th className="text-right px-4 py-3">Engagement</th>
                <th className="text-right px-4 py-3">Followers gagnés</th>
                <th className="text-right px-4 py-3">Score</th>
                <th className="text-left px-4 py-3">Tags</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => (
                <tr key={m.id} data-testid={`media-row-${i}`} className="border-b border-[#1E1E24] last:border-0 hover:bg-white/[0.02]">
                  <td className="px-4 py-3"><span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 bg-white/5 text-zinc-300 rounded-sm">{m.media_type}</span></td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{m.published_at?.slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 max-w-xs"><div className="text-white truncate text-sm">{m.caption || "—"}</div></td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-300">{(m.views || 0).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-300">{(m.reach || 0).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-300">{m.engagement_rate || 0}%</td>
                  <td className={`px-4 py-3 text-right font-mono ${m.new_followers_attributed > 0 ? "text-green-400" : "text-zinc-500"}`}>
                    {m.new_followers_attributed > 0 ? "+" : ""}{m.new_followers_attributed || 0}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ScoreBadge score={m.performance_score} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(m.tags || []).slice(0, 2).map(t => (
                        <span key={t} className="text-[9px] font-mono uppercase px-1.5 py-0.5 bg-[#CCFF00]/10 text-[#CCFF00] border border-[#CCFF00]/20 rounded-sm">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(m.id)} className="text-zinc-600 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddMediaModal accounts={accounts} preselected={selected?.id || ""} onClose={() => setShowAdd(false)} onSuccess={() => { setShowAdd(false); refresh(); }} />}
    </div>
  );
}

const ScoreBadge = ({ score }) => {
  const s = score || 0;
  const color = s >= 80 ? "#10B981" : s >= 60 ? "#CCFF00" : s >= 40 ? "#F59E0B" : "#EF4444";
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs px-2 py-0.5 rounded-sm" style={{ backgroundColor: color + "20", color }}>
      {s}
    </span>
  );
};

function AddMediaModal({ accounts, preselected, onClose, onSuccess }) {
  const [f, setF] = useState({
    account_id: preselected || (accounts[0]?.id || ""), media_type: "reel",
    caption: "", permalink: "", thumbnail_url: "",
    published_at: new Date().toISOString().slice(0, 10),
    views: 0, reach: 0, likes: 0, comments: 0, saves: 0, shares: 0, new_followers_attributed: 0,
    tags: [], internal_notes: "",
  });
  const [loading, setLoading] = useState(false);

  const toggleTag = (t) => setF({ ...f, tags: f.tags.includes(t) ? f.tags.filter(x => x !== t) : [...f.tags, t] });

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const data = { ...f, views: Number(f.views), reach: Number(f.reach), likes: Number(f.likes),
                     comments: Number(f.comments), saves: Number(f.saves), shares: Number(f.shares),
                     new_followers_attributed: Number(f.new_followers_attributed) };
      await api.igCreateMedia(data); toast.success("Contenu ajouté"); onSuccess();
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
        className="bg-[#0A0A0C] border border-[#1E1E24] rounded-sm p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-media-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl text-white">Nouveau contenu</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500">Compte</label>
            <select data-testid="m-account" required value={f.account_id} onChange={e => setF({...f, account_id: e.target.value})}
              className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none">
              {accounts.map(a => <option key={a.id} value={a.id}>@{a.handle}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500">Type</label>
            <select data-testid="m-type" value={f.media_type} onChange={e => setF({...f, media_type: e.target.value})}
              className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none">
              {TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-mono uppercase text-zinc-500">Caption</label>
            <textarea data-testid="m-caption" rows={2} value={f.caption} onChange={e => setF({...f, caption: e.target.value})}
              className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500">Lien (permalink)</label>
            <input data-testid="m-permalink" value={f.permalink} onChange={e => setF({...f, permalink: e.target.value})}
              className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500">Date publication</label>
            <input data-testid="m-date" type="date" value={f.published_at} onChange={e => setF({...f, published_at: e.target.value})}
              className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
          </div>
        </div>

        <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mt-4">
          {["views", "reach", "likes", "comments", "saves", "shares", "new_followers_attributed"].map(k => (
            <div key={k}>
              <label className="text-[10px] font-mono uppercase text-zinc-500">{k.replace("_", " ")}</label>
              <input type="number" value={f[k]} onChange={e => setF({...f, [k]: e.target.value})}
                className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
            </div>
          ))}
        </div>

        <div className="mt-4">
          <label className="text-[10px] font-mono uppercase text-zinc-500">Tags</label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {TAGS.map(t => (
              <button key={t} type="button" onClick={() => toggleTag(t)}
                className={`text-[10px] font-mono uppercase px-2 py-1 rounded-sm border transition ${
                  f.tags.includes(t) ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-[#1E1E24] text-zinc-400 hover:border-white/30"
                }`}>{t}</button>
            ))}
          </div>
        </div>

        <button disabled={loading} type="submit" data-testid="m-submit"
          className="w-full mt-5 bg-[#CCFF00] text-black font-medium py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}{loading ? "..." : "Ajouter le contenu"}
        </button>
      </form>
    </div>
  );
}
